<template>
  <Listbox
    :model-value="selectedItem"
    class="relative"
    :class="[containerClass]"
    :style="[containerStyle]"
    as="div"
    @update:modelValue="
      selectedItem = $event;
      onItemSelected();
    "
  >
    <div class="eds-field w-full">
      <ListboxLabel class="field-label">{{ label }}</ListboxLabel>
      <div
        class="field-control eds-input"
        :class="[`input-${state}`, fieldControlClass]"
        :style="[fieldControlStyle]"
      >
        <ListboxButton
          class="input-element input-dropdown"
          :class="[inputButtonClass]"
          :style="inputButtonStyle"
          :disabled="state === 'disabled'"
          as="button"
        >
          <span
            :class="[inputValueClass]"
            :style="[inputValueStyle]"
            style="
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            "
            >{{ selectedItem }}</span
          >
        </ListboxButton>
      </div>
    </div>
    <transition
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        role="popover"
        class="eds-popover absolute w-full block m-0 p-0 focus:outline-none"
        :class="[popoverClass]"
        :style="[popoverStyle]"
        as="div"
        tabindex="1"
      >
        <div v-if="filterable" class="mb-8 pt-4 pr-4 pl-4">
          <Textbox
            id="dropdownValueTextBox"
            v-model="filterValue"
            :placeholder="placeholder"
            @update:modelValue="filterOptionsHandler"
          />
        </div>
        <div
          v-if="options.length"
          class="eds-field w-full overflow-auto"
          style="max-height: 280px"
        >
          <div class="field-control eds-control-list">
            <ListboxOption
              v-for="(item, index) in options"
              v-slot="{ active }"
              :key="index"
              :value="getOptionValue(item)"
              as="template"
            >
              <label
                :class="[active ? activeItemClass : '', fieldLabelClass]"
                :style="fieldLabelStyle"
              >
                <span>{{ getOptionLabel(item) }}</span>
              </label>
            </ListboxOption>
          </div>
        </div>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script lang="ts">
  import { defineComponent, ref, StyleValue, toRefs } from 'vue';
  import {
    Listbox,
    ListboxLabel,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
  } from '@headlessui/vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';

  export default defineComponent({
    name: 'Dropdown',
    emits: ['update:modelValue'],
    props: {
      label: {
        type: String,
        required: false,
        default: '',
      },
      items: {
        type: Array,
        required: false,
        default: () => [],
      },
      state: {
        type: String,
        required: false,
        default: 'basic',
        validator: (value: string) => {
          return ['basic', 'warning', 'error', 'disabled'].includes(value);
        },
      },
      filterable: {
        type: Boolean,
        required: false,
        default: false,
      },
      placeholder: {
        type: String,
        required: false,
        default: 'Type to filter options...',
      },
      getOptionLabel: {
        type: Function,
        required: false,
        default: (option: any) => {
          if (typeof option === 'object') {
            // eslint-disable-next-line no-prototype-builtins
            if (!option.hasOwnProperty('name')) {
              return console.warn(
                `[vue-select warn]: Label key "option.name" does not` +
                  ` exist in options object ${JSON.stringify(option)}.\n` +
                  'https://vue-select.org/api/props.html#getoptionlabel'
              );
            }
            return option['name'];
          }
          return option;
        },
      },
      getOptionValue: {
        type: Function,
        default: (option: any) => {
          if (typeof option !== 'object') {
            return option;
          }
          return option['name'];
        },
      },
      filterKey: {
        type: String,
        required: false,
        default: 'name',
      },
      containerClass: {
        type: String,
        required: false,
        default: '',
      },
      containerStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      fieldClass: {
        type: String,
        required: false,
        default: '',
      },
      fieldStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      fieldControlClass: {
        type: String,
        required: false,
        default: '',
      },
      fieldControlStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      inputButtonClass: {
        type: String,
        required: false,
        default: '',
      },
      inputButtonStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      inputValueClass: {
        type: String,
        required: false,
        default: '',
      },
      inputValueStyle: {
        type: Object as () => StyleValue,
        required: false,
        default: () => ({}),
      },
      popoverClass: {
        type: String,
        required: false,
        default: '',
      },
      popoverStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      fieldLabelClass: {
        type: String,
        required: false,
        default: '',
      },
      fieldLabelStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      activeItemClass: {
        type: String,
        required: false,
        default: '',
      },
      modelValue: {
        type: [String, Number],
        required: false,
        default: '',
      },
    },
    components: {
      Listbox,
      ListboxLabel,
      ListboxButton,
      ListboxOptions,
      ListboxOption,
      Textbox,
    },
    setup: (props, { emit }) => {
      const filterValue = ref('');
      const { modelValue, items, getOptionLabel } = toRefs(props);
      let selectedItem = ref(modelValue.value);
      let tempOptions = ref(items.value);
      let options = ref(items.value);

      const onItemSelected = () => {
        emit('update:modelValue', selectedItem.value);
      };

      const filterOptionsHandler = () => {
        let filteredRows = [];
        if (filterValue.value.length) {
          filteredRows = tempOptions.value.filter((elem: any) => {
            if (typeof elem === 'object') {
              const val: string = getOptionLabel.value(elem);
              return val
                ?.toLowerCase()
                .includes(filterValue.value.toLowerCase());
            } else if (typeof elem === 'string') {
              const val: string = elem;
              return val.toLowerCase().match(filterValue.value.toLowerCase());
            } else {
              const val: string = elem;
              return val.toLowerCase() === filterValue.value.toLowerCase();
            }
          });
        } else {
          filteredRows = tempOptions.value;
        }
        options.value = filteredRows;
      };

      return {
        filterValue,
        selectedItem,
        options,
        onItemSelected,
        filterOptionsHandler,
      };
    },
  });
</script>
<style></style>
